import { KeyboardEvent, useMemo } from 'react';
import styled from 'styled-components';
import { Clef } from '../../../../../common/src/learn_rules.ts';
import { SolfegePhrase } from '../../../../../common/src/sing_in_solfege.ts';
import { hearSyllableLabel } from '../interval/meet_syllables_screen.tsx';
import { shortScreen } from '../learn_screen_parts.tsx';
import { shortScreenStaffWidth } from '../pitch/note_screen_parts.tsx';
import { PitchDotColor, pitchDotColorCss } from '../pitch/pitch_colors.ts';
import {
  solfegeNoteColors,
  solfegePlayheadColor,
  solfegeQuietDotColor,
  solfegeResultColorCss,
  solfegeSyllableColors,
} from './solfege_colors.ts';
import { SolfegeNoteDisplay } from './solfege_session.ts';
import { SolfegeSign, solfegeSignShapes, solfegeSignTransform } from './solfege_signs.ts';
import {
  solfegeLaneRect,
  solfegeNoteGlyph,
  solfegePitchY,
  SolfegeStaffGeometry,
  solfegeStaffGeometry,
  solfegeStaffLayout,
  solfegeStepY,
  solfegeTimeX,
} from './solfege_staff_geometry.ts';
import { SolfegeTracePath, solfegeTraceTuning } from './solfege_trace.ts';

// The staff Sing in Solfège draws a hymn phrase on (issue #3722), from the "Sing in Solfège
// (listen)", "(sing along)" and "(result)" artboards: Pitch Match's five lines with no letters, each
// note of the phrase a notehead with a stem at the step its degree puts it on, in a slot as long as
// the note lasts, its syllable under it. A major phrase has no clef and no key signature; a minor
// one has the singer's clef and do's minor key signature at the left, in the staff's ink, and a
// chromatic note its sharp, flat or natural (issue #3772, solfege_signs.ts). Each
// note takes the colour of its state (solfegeNoteDisplays): grey still to come, purple and larger as
// it sounds or is sung, dark once played, then green, amber or red as its grade comes in. Behind
// each note a soft lane of its colour runs the length of its slot, so the singer sees how long to
// hold it and has something to read the line against.
//
// While the singer sings, the dot moves across by the clock at the height of their voice, and the
// line it leaves behind (solfege_trace.ts) is coloured by how far the voice was from the note at
// that moment; with no voice the dot rides the current note's lane as a hollow ring, so the singer
// always sees where the phrase is. The line stays on the result, and during "Hear mine" a slim
// playhead runs along it in time with the playback.
//
// Each note is written as its real value, the phrase's beat the quarter note: a filled or open
// head, a stem, flags (never beams: each sung syllable carries its own, as vocal music is engraved,
// and each note keeps its own colour) and an augmentation dot, all in the note's colour and scaled
// up with the current note (solfegeNoteGlyph).
//
// Layers, bottom to top: the staff and ledger lines, a minor phrase's clef and key signature, the
// lanes, the notes with their accidentals, stems, flags, dots and syllables, the line, the open heads' rings again (so a half note the line runs through
// still reads as open), the playhead, the dot. Every number comes from solfege_staff_geometry.ts, so
// this file only turns geometry into SVG; it holds no state and knows nothing about audio. The
// syllables are drawn inside the SVG, as the artboards draw them, so they scale with the staff and
// the geometry's check that they never collide holds at any width.

export const solfegeStaffTestId = 'solfege-staff';
export const solfegeNoteTestId = 'solfege-note';
export const solfegeNoteHitTestId = 'solfege-note-hit';
export const solfegeLedgerTestId = 'solfege-ledger';
export const solfegeLaneTestId = 'solfege-lane';
export const solfegeNoteheadTestId = 'solfege-notehead';
export const solfegeStemTestId = 'solfege-stem';
export const solfegeFlagTestId = 'solfege-flag';
export const solfegeAugmentationDotTestId = 'solfege-augmentation-dot';
export const solfegeOpenHeadsOverTraceTestId = 'solfege-open-heads-over-trace';
export const solfegeSyllableTestId = 'solfege-syllable';
export const solfegeTraceTestId = 'solfege-trace';
export const solfegeTracePathTestId = 'solfege-trace-path';
export const solfegeDotTestId = 'solfege-dot';
export const solfegePlayheadTestId = 'solfege-playhead';
export const solfegeClefTestId = 'solfege-clef';
export const solfegeKeySignatureTestId = 'solfege-key-signature';
export const solfegeKeyAccidentalTestId = 'solfege-key-accidental';
export const solfegeAccidentalTestId = 'solfege-accidental';

const staffLineColor = 'var(--color-text)';
// The page behind the staff: the dot's ring is cut out of it, so it reads over a staff line
const paperColor = 'var(--color-background)';
const semiBoldFont = 'Jost-SemiBold, Arial, sans-serif';
const regularFont = 'Jost-Regular, Arial, sans-serif';

const lineSteps = [0, 2, 4, 6, 8];

// The singer, as the session's solfegeLiveDot gives it: across by `intoMs` (phraseNoteSpans'
// scale), and when `voiced` down by `midi`, in the scorer's dot colour; otherwise on note
// `noteIndex`'s lane
export interface SolfegeStaffDot {
  intoMs: number;
  noteIndex: number;
  voiced: boolean;
  midi?: number;
  color?: PitchDotColor;
}

// The geometry the staff draws a phrase with, for a screen that also builds the line from it
export function useSolfegeStaffGeometry(phrase: SolfegePhrase, homeMidi: number, clef: Clef): SolfegeStaffGeometry {
  return useMemo(() => solfegeStaffGeometry({ phrase, homeMidi, clef }), [phrase, homeMidi, clef]);
}

export const SolfegeStaff = ({
  clef,
  homeMidi,
  phrase,
  displays,
  trace = [],
  dot,
  playheadMs,
  onTapNote,
}: {
  clef: Clef; // the singer's, from clefForVoicePart
  homeMidi: number; // the phrase's do
  phrase: SolfegePhrase;
  displays: SolfegeNoteDisplay[]; // one per note, from solfegeNoteDisplays
  trace?: SolfegeTracePath[]; // the singer's line, from solfegeTracePaths
  dot?: SolfegeStaffDot;
  playheadMs?: number; // "Hear mine": how far the playback is into the take
  // A tap on a note - its syllable, notehead or anywhere in its slot - plays it; without this the
  // notes take no taps
  onTapNote?: (index: number) => void;
}) => {
  const layout = solfegeStaffLayout;
  const geometry = useSolfegeStaffGeometry(phrase, homeMidi, clef);
  const { band, height, notes } = geometry;
  const displayOf = (index: number): SolfegeNoteDisplay => displays[index] ?? 'upcoming';
  // Each note's glyph, regular and as the current note, built once per geometry: the staff renders
  // 60 to 94 times a second during a take and a playback, and each render only picks a variant
  const glyphVariants = useMemo(
    () => notes.map((note) => ({ regular: solfegeNoteGlyph(note, false), current: solfegeNoteGlyph(note, true) })),
    [notes],
  );
  const glyphs = notes.map(
    (note) => glyphVariants[note.index][displayOf(note.index) === 'current' ? 'current' : 'regular'],
  );
  const dotNote = dot ? notes[Math.max(0, Math.min(notes.length - 1, dot.noteIndex))] : undefined;
  return (
    <StaffBox>
      <Svg
        data-testid={solfegeStaffTestId}
        data-clef={clef}
        data-min-step={band.minStep}
        data-max-step={band.maxStep}
        data-mode={geometry.mode}
        data-crowded={geometry.crowded ? 'true' : 'false'}
        data-scale={geometry.scale}
        viewBox={`0 0 ${geometry.width} ${height}`}
      >
        {lineSteps.map((step) => (
          <line
            key={step}
            x1={layout.lineX1}
            y1={solfegeStepY(geometry, step)}
            x2={geometry.lineX2}
            y2={solfegeStepY(geometry, step)}
            stroke={staffLineColor}
            strokeWidth={layout.lineWidth}
          />
        ))}
        {/* Each ledger line is a short line centred on its own notehead, as printed music draws it */}
        {notes.flatMap((note) =>
          note.ledgerSteps.map((ledger) => (
            <line
              key={`${note.index}-${ledger}`}
              data-testid={solfegeLedgerTestId}
              data-index={note.index}
              data-step={ledger}
              x1={note.x - note.ledgerHalfWidth}
              y1={solfegeStepY(geometry, ledger)}
              x2={note.x + note.ledgerHalfWidth}
              y2={solfegeStepY(geometry, ledger)}
              stroke={staffLineColor}
              strokeWidth={layout.lineWidth}
            />
          )),
        )}
        {geometry.signature && (
          <g data-testid={solfegeKeySignatureTestId} fill={staffLineColor} style={{ pointerEvents: 'none' }}>
            <SignPath testId={solfegeClefTestId} sign={geometry.signature.clef} data-clef={clef} />
            {geometry.signature.signs.map((sign) => (
              <SignPath
                key={sign.letter}
                testId={solfegeKeyAccidentalTestId}
                sign={sign}
                data-accidental={sign.accidental}
                data-letter={sign.letter}
                data-step={sign.step}
              />
            ))}
          </g>
        )}
        {notes.map((note) => {
          const lane = solfegeLaneRect(note);
          return (
            <rect
              key={note.index}
              data-testid={solfegeLaneTestId}
              data-index={note.index}
              data-display={displayOf(note.index)}
              x={lane.x}
              y={lane.y}
              width={lane.width}
              height={lane.height}
              rx={lane.height / 2}
              fill={solfegeNoteColors[displayOf(note.index)]}
              opacity={layout.laneOpacity}
            />
          );
        })}
        {notes.map((note) => {
          const display = displayOf(note.index);
          const emphasized = display === 'current';
          const color = solfegeNoteColors[display];
          const glyph = glyphs[note.index];
          const { head, stem, dot: augmentationDot } = glyph;
          const fontSize = emphasized ? geometry.syllableSize.emphasized : geometry.syllableSize.regular;
          const playable = onTapNote !== undefined;
          const play = () => onTapNote?.(note.index);
          return (
            <NoteGroup
              key={note.index}
              data-testid={solfegeNoteTestId}
              data-index={note.index}
              data-degree={note.degree}
              data-alteration={note.alteration}
              data-chromatic={note.chromatic ? 'true' : 'false'}
              data-accidental={note.accidental ?? 'none'}
              data-syllable={note.syllable}
              data-step={note.step}
              data-stem={note.stem}
              data-value={note.value.name}
              data-head={note.value.head}
              data-flags={note.value.flags}
              data-dotted={note.value.dotted ? 'true' : 'false'}
              data-display={display}
              data-emphasized={emphasized ? 'true' : 'false'}
              data-playable={playable ? 'true' : 'false'}
              role={playable ? 'button' : undefined}
              tabIndex={playable ? 0 : undefined}
              aria-label={playable ? hearSyllableLabel(note.syllable) : undefined}
              onClick={playable ? play : undefined}
              onKeyDown={playable ? (event: KeyboardEvent) => pressed(event, play) : undefined}
            >
              {/* The note's tap area: its whole slot, the staff's full height, painted transparent
                  so it draws nothing and still takes pointer events */}
              {playable && (
                <rect
                  data-testid={solfegeNoteHitTestId}
                  data-index={note.index}
                  aria-hidden="true"
                  x={note.slot.x1}
                  y={0}
                  width={note.slot.x2 - note.slot.x1}
                  height={height}
                  fill="transparent"
                />
              )}
              {glyph.accidental && (
                <SignPath
                  testId={solfegeAccidentalTestId}
                  sign={glyph.accidental}
                  data-index={note.index}
                  data-accidental={glyph.accidental.shape}
                  fill={color}
                />
              )}
              {head.ringD ? (
                // An open head: a ring in the note's colour, the staff showing through its hole
                <path
                  data-testid={solfegeNoteheadTestId}
                  data-index={note.index}
                  data-open="true"
                  d={head.ringD}
                  fillRule="evenodd"
                  fill={color}
                />
              ) : (
                <ellipse
                  data-testid={solfegeNoteheadTestId}
                  data-index={note.index}
                  data-open="false"
                  cx={head.cx}
                  cy={head.cy}
                  rx={head.rx}
                  ry={head.ry}
                  transform={`rotate(${head.rotation} ${head.cx} ${head.cy})`}
                  fill={color}
                />
              )}
              {stem && (
                <line
                  data-testid={solfegeStemTestId}
                  data-index={note.index}
                  x1={stem.x}
                  y1={stem.y1}
                  x2={stem.x}
                  y2={stem.y2}
                  stroke={color}
                  strokeWidth={layout.stemWidth}
                />
              )}
              {/* A flag is known by its outline: no two of a note's flags stand in the same place */}
              {glyph.flags.map((d) => (
                <path key={d} data-testid={solfegeFlagTestId} data-index={note.index} d={d} fill={color} />
              ))}
              {augmentationDot && (
                <circle
                  data-testid={solfegeAugmentationDotTestId}
                  data-index={note.index}
                  cx={augmentationDot.cx}
                  cy={augmentationDot.cy}
                  r={augmentationDot.r}
                  fill={color}
                />
              )}
              <text
                data-testid={solfegeSyllableTestId}
                data-index={note.index}
                data-display={display}
                x={note.x}
                y={geometry.syllableY}
                textAnchor="middle"
                fontFamily={emphasized ? semiBoldFont : regularFont}
                fontSize={fontSize}
                fill={solfegeSyllableColors[display]}
              >
                {note.syllable}
              </text>
            </NoteGroup>
          );
        })}
        {trace.length > 0 && (
          <g data-testid={solfegeTraceTestId} fill="none" style={{ pointerEvents: 'none' }}>
            {/* A run is known by where it starts: its fragment and its first point's time */}
            {trace.map((path) => (
              <path
                key={`${path.fragment}-${path.points[0]?.intoMs}`}
                data-testid={solfegeTracePathTestId}
                data-color={path.color}
                data-fragment={path.fragment}
                d={path.d}
                stroke={solfegeResultColorCss[path.color]}
                strokeWidth={solfegeTraceTuning.strokeWidth}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            ))}
          </g>
        )}
        {/* An open head the line runs through is drawn again over the line, ring only, so it still
            reads as open: the line shows through its hole instead of filling it */}
        {trace.length > 0 && glyphs.some((glyph) => glyph.head.ringD) && (
          <g data-testid={solfegeOpenHeadsOverTraceTestId} style={{ pointerEvents: 'none' }}>
            {notes.map((note) => {
              const ringD = glyphs[note.index].head.ringD;
              return (
                ringD && (
                  <path
                    key={note.index}
                    data-index={note.index}
                    d={ringD}
                    fillRule="evenodd"
                    fill={solfegeNoteColors[displayOf(note.index)]}
                  />
                )
              );
            })}
          </g>
        )}
        {playheadMs !== undefined && (
          <line
            data-testid={solfegePlayheadTestId}
            data-into-ms={Math.round(playheadMs)}
            x1={solfegeTimeX(geometry, playheadMs)}
            y1={solfegeStepY(geometry, Math.min(band.maxStep, 10))}
            x2={solfegeTimeX(geometry, playheadMs)}
            y2={solfegeStepY(geometry, Math.max(band.minStep, -2))}
            stroke={solfegePlayheadColor}
            strokeWidth={layout.playheadWidth}
            strokeLinecap="round"
            style={{ pointerEvents: 'none' }}
          />
        )}
        {dot && dotNote && <Dot geometry={geometry} dot={dot} laneY={dotNote.laneY} />}
      </Svg>
    </StaffBox>
  );
};

// A sign (solfege_signs.ts): its outline and any balls, drawn at its place and size by a transform,
// in the fill it is given or the group's
const SignPath = ({
  testId,
  sign,
  fill,
  ...data
}: {
  testId: string;
  sign: SolfegeSign;
  fill?: string;
  [dataAttribute: `data-${string}`]: string | number;
}) => {
  const shape = solfegeSignShapes[sign.shape];
  return (
    <g data-testid={testId} data-shape={sign.shape} transform={solfegeSignTransform(sign)} fill={fill} {...data}>
      <path d={shape.d} />
      {shape.balls.map((ball) => (
        <circle key={`${ball.cx} ${ball.cy}`} cx={ball.cx} cy={ball.cy} r={ball.r} />
      ))}
    </g>
  );
};

// Enter and Space press a note, as they would a button
function pressed(event: KeyboardEvent, play: () => void): void {
  if (event.key !== 'Enter' && event.key !== ' ') {
    return;
  }
  event.preventDefault();
  play();
}

// The singer's dot: the artboards' filled circle on a ring of paper with a halo while a voice is
// heard, a hollow grey ring on the current note's lane while none is. It moves on every frame, with
// no CSS transition, for the reason Interval Training's dot has none (a transition retargeted each
// frame never completes in WebKit, issue #3587). Where it is, is in the transform, which is what
// tests read. A tap passes through it to the note under it.
const Dot = ({ geometry, dot, laneY }: { geometry: SolfegeStaffGeometry; dot: SolfegeStaffDot; laneY: number }) => {
  const layout = solfegeStaffLayout;
  const x = solfegeTimeX(geometry, dot.intoMs);
  const voiced = dot.voiced && dot.midi !== undefined;
  const y = voiced ? solfegePitchY(geometry, dot.midi as number) : laneY;
  const color = pitchDotColorCss[dot.color ?? 'yellow'];
  return (
    <g
      data-testid={solfegeDotTestId}
      data-index={dot.noteIndex}
      data-voiced={voiced ? 'true' : 'false'}
      data-color={voiced ? (dot.color ?? 'yellow') : 'quiet'}
      style={{ transform: `translate(${x}px, ${y}px)`, pointerEvents: 'none' }}
    >
      {voiced ? (
        <>
          <circle
            cx={0}
            cy={0}
            r={layout.dotRadius}
            fill={color}
            stroke={paperColor}
            strokeWidth={layout.dotStrokeWidth}
          />
          <circle
            cx={0}
            cy={0}
            r={layout.haloRadius}
            fill="none"
            stroke={color}
            strokeWidth={layout.haloStrokeWidth}
            opacity={layout.haloOpacity}
          />
        </>
      ) : (
        <circle
          cx={0}
          cy={0}
          r={layout.dotRadius - layout.dotStrokeWidth / 2}
          fill={paperColor}
          stroke={solfegeQuietDotColor}
          strokeWidth={layout.dotStrokeWidth}
        />
      )}
    </g>
  );
};

// The staff is drawn at the artboards' 350 units and scaled to whatever the screen gives it, as
// Interval Training's is, syllables and all; a phrase drawn smaller has a wider viewBox in the same
// box (solfegeStaffGeometry's scale)
const StaffBox = styled.div`
  width: 100%;
  max-width: ${solfegeStaffLayout.width}px;
  margin: 0 auto;

  ${shortScreen} {
    max-width: ${shortScreenStaffWidth}px;
  }
`;

const Svg = styled.svg`
  display: block;
  width: 100%;
  height: auto;
`;

// A note takes a tap only when the screen gives it one, and dims while pressed
const NoteGroup = styled.g`
  -webkit-tap-highlight-color: transparent;
  outline: none;

  &[data-playable='true'] {
    cursor: pointer;
  }

  &[data-playable='true']:active {
    opacity: 0.6;
  }

  &[data-playable='true']:focus-visible text {
    text-decoration: underline;
  }
`;
